/**
 * Nold Go — Module Covoiturage (style BlaBlaCar longue distance).
 *
 * 4 pages exposées via App.js :
 *   /covoiturage              → CarpoolSearch  : recherche + résultats
 *   /covoiturage/publier      → CarpoolPublish : conducteur publie un trajet
 *   /covoiturage/mes-trajets  → CarpoolMine    : dashboard conducteur
 *   /covoiturage/trajet/:id   → CarpoolTrip    : détails + réservation Stripe
 *
 * Contraintes métier appliquées côté UI :
 *   • Autocomplete villes limitée à la liste blanche backend (/carpool/cities).
 *   • Le prix par place s'ajuste automatiquement au plafond (coût réel ÷ places).
 *   • Commission 10 % annoncée à l'utilisateur au moment de la publication.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Car, Users, MapPin, Calendar, Clock, ArrowRight, Euro, Loader2, Info, CheckCircle2, ShieldCheck, AlertTriangle, Upload, ExternalLink, UserCircle2 } from "lucide-react";
import { DriverSnapshot } from "./CarpoolProfile";
import { CarpoolChatModal } from "./CarpoolLanding";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SEOHead from "../components/SEOHead";

const COST_PER_KM_EUR = 0.12;

function CityInput({ value, onChange, cities, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const filtered = cities.filter((c) => c.toLowerCase().includes((value || "").toLowerCase()));
  return (
    <div className="relative">
      <input data-testid={testid} value={value} onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-4 text-sm focus:border-[#059669] outline-none" />
      {open && filtered.length > 0 && (
        <ul className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-[#E2DFD8] rounded-xl shadow-lg max-h-60 overflow-auto">
          {filtered.map((c) => (
            <li key={c} onMouseDown={() => onChange(c)}
              className="px-4 py-2 hover:bg-[#F0EBE1] cursor-pointer text-sm">{c}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -------------------------------------------------------- SEARCH
export function CarpoolSearch() {
  const [cities, setCities] = useState([]);
  const [from, setFrom] = useState("Bruxelles");
  const [to, setTo] = useState("Paris");
  const [date, setDate] = useState("");
  const [seats, setSeats] = useState(1);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/carpool/cities").then(r => setCities(r.data.cities || [])); }, []);

  const search = async () => {
    setLoading(true);
    try {
      const r = await api.get("/carpool/search", { params: { from_city: from, to_city: to, date: date || undefined, seats, alt_dates: true } });
      setResults(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-search-page">
      <SEOHead title="Covoiturage longue distance — Nold Go"
        description="Réservez une place en covoiturage entre grandes villes européennes. Bruxelles, Paris, Amsterdam, Berlin, Londres, Madrid, Rome — au prix coûtant sans bénéfice conducteur."
        canonical="/covoiturage" />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#059669] flex items-center justify-center"><Car className="w-7 h-7 text-white" /></div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F26419] font-bold">Nold Go · Covoiturage</p>
            <h1 className="font-head text-3xl sm:text-4xl font-black text-[#1A1A1A]">Voyagez entre grandes villes</h1>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#E2DFD8] space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Départ</label>
              <CityInput testid="carpool-from" value={from} onChange={setFrom} cities={cities} placeholder="Bruxelles" /></div>
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Arrivée</label>
              <CityInput testid="carpool-to" value={to} onChange={setTo} cities={cities} placeholder="Paris" /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Date</label>
              <input data-testid="carpool-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm outline-none focus:border-[#059669]" /></div>
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Places</label>
              <input data-testid="carpool-seats" type="number" min="1" max="8" value={seats} onChange={(e) => setSeats(Number(e.target.value))}
                className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm outline-none focus:border-[#059669]" /></div>
            <button data-testid="carpool-search" onClick={search} disabled={loading}
              className="sm:col-span-1 col-span-2 h-12 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm disabled:opacity-50 mt-6 sm:mt-6">
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Rechercher"}
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-between text-xs text-[#75807A] pt-1 gap-2">
            <span>💡 Prix au coût réel · 10 % commission Nold Go</span>
            <div className="flex items-center gap-3 flex-wrap">
              <Link to="/covoiturage/mes-reservations" data-testid="link-my-bookings" className="text-[#059669] font-bold hover:underline">Mes réservations</Link>
              <Link to="/covoiturage/mes-alertes" data-testid="link-my-alerts" className="text-[#F26419] font-bold hover:underline">🔔 Mes alertes</Link>
              <Link to="/covoiturage/profil" data-testid="link-profile" className="text-[#F26419] font-bold hover:underline">Mon profil</Link>
              <Link to="/covoiturage/publier" className="text-[#F26419] font-bold hover:underline">Proposer un trajet →</Link>
              <Link to="/covoiturage/conditions" data-testid="link-conditions" className="text-[#75807A] hover:underline">Conditions</Link>
            </div>
          </div>
        </div>

        {results && (
          <div className="mt-6 space-y-3" data-testid="carpool-results">
            <p className="text-xs text-[#75807A]">Distance estimée : <b>{results.distance_km} km</b> · {results.trips.length} trajet(s) disponible(s)</p>
            {results.trips.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] p-8 text-center text-sm text-[#75807A]">
                Aucun trajet trouvé. Soyez le premier à en publier un ! <Link to="/covoiturage/publier" className="text-[#059669] font-bold">Publier →</Link>
              </div>
            ) : results.trips.map((t) => (
              <Link key={t.id} to={`/covoiturage/trajet/${t.id}`} data-testid={`carpool-trip-${t.id}`}
                className="block bg-white rounded-2xl p-4 border-2 border-[#E2DFD8] hover:border-[#059669] transition-colors">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 text-lg font-bold text-[#1A1A1A]">
                      {t.from_city} <ArrowRight className="w-4 h-4 text-[#75807A]" /> {t.to_city}
                    </div>
                    <div className="text-xs text-[#75807A] mt-1 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.date}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.time} · ~{t.duration_h}h</span>
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {t.seats_left}/{t.seats_total} places</span>
                    </div>
                    <div className="mt-2"><DriverSnapshot trip={t} compact /></div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-[#059669]">{t.price_per_seat_eur.toFixed(2)}€</div>
                    <div className="text-xs text-[#75807A]">/ place</div>
                  </div>
                </div>
              </Link>
            ))}
            {results.trips.length === 0 && results.alt_trips && results.alt_trips.length > 0 && (
              <div className="pt-4" data-testid="carpool-alt-trips">
                <div className="text-xs text-[#F26419] font-bold uppercase tracking-widest mb-2">💡 Trajets à ±2 jours disponibles</div>
                {results.alt_trips.map((t) => (
                  <Link key={t.id} to={`/covoiturage/trajet/${t.id}`} data-testid={`carpool-alt-trip-${t.id}`}
                    className="block bg-white rounded-2xl p-4 border-2 border-dashed border-[#F26419]/40 hover:border-[#F26419] transition-colors mb-2">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[220px]">
                        <div className="flex items-center gap-2 text-lg font-bold text-[#1A1A1A]">
                          {t.from_city} <ArrowRight className="w-4 h-4 text-[#75807A]" /> {t.to_city}
                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#F26419]/10 text-[#F26419] font-bold">
                            {t.date_offset_days > 0 ? `+${t.date_offset_days} j` : `${t.date_offset_days} j`}
                          </span>
                        </div>
                        <div className="text-xs text-[#75807A] mt-1 flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.date}</span>
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.time}</span>
                          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {t.seats_left}/{t.seats_total}</span>
                        </div>
                        <div className="mt-2"><DriverSnapshot trip={t} compact /></div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-black text-[#F26419]">{t.price_per_seat_eur.toFixed(2)}€</div>
                        <div className="text-xs text-[#75807A]">/ place</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------- PUBLISH
export function CarpoolPublish() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ from_city: "Bruxelles", to_city: "Paris", date: "", time: "08:00", seats_total: 3, price_per_seat_eur: 25, meeting_point: "" });
  const [recurringWeeks, setRecurringWeeks] = useState(1);
  const [busy, setBusy] = useState(false);
  const [verif, setVerif] = useState(null);

  useEffect(() => { api.get("/carpool/cities").then(r => setCities(r.data.cities || [])); }, []);
  useEffect(() => {
    if (user) api.get("/carpool/verification/status").then(r => setVerif(r.data)).catch(() => {});
  }, [user]);

  // Blocage strict côté UI : sans upload docs, on ne peut pas publier.
  const docsMissing = verif && (verif.status === "none" || !verif.status);

  // Calcul en direct du plafond de prix (coût / places)
  const priceCap = useMemo(() => {
    // Approx : distance haversine côté client + 25 %
    const c1 = { Bruxelles: [50.85, 4.35], Paris: [48.86, 2.35], Amsterdam: [52.37, 4.90], Luxembourg: [49.61, 6.13],
      Cologne: [50.94, 6.96], Londres: [51.51, -0.13], Berlin: [52.52, 13.40], Madrid: [40.42, -3.70], Rome: [41.90, 12.50] };
    const a = c1[form.from_city], b = c1[form.to_city];
    if (!a || !b) return null;
    const R = 6371, toRad = x => x * Math.PI / 180;
    const dlat = toRad(b[0] - a[0]), dlng = toRad(b[1] - a[1]);
    const d = 2 * R * Math.asin(Math.sqrt(Math.sin(dlat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dlng / 2) ** 2)) * 1.25;
    return { distance_km: Math.round(d * 10) / 10, cap: Math.round((d * COST_PER_KM_EUR / form.seats_total) * 100) / 100 };
  }, [form.from_city, form.to_city, form.seats_total]);

  const submit = async () => {
    if (!user) { toast.error("Connectez-vous d'abord pour publier"); nav("/login"); return; }
    if (!form.meeting_point.trim()) { toast.error("Précisez un point de rendez-vous"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/carpool/trips", { ...form, recurring_weeks: recurringWeeks });
      const count = data.trips_created || 1;
      toast.success(
        count > 1 ? `${count} trajets hebdomadaires publiés !`
        : data.cap_applied ? "Publié ! Prix ajusté au plafond légal (coût réel)." : "Trajet publié !"
      );
      nav(`/covoiturage/trajet/${data.trip.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-publish-page">
      <SEOHead title="Proposer un trajet — Nold Go Covoiturage" description="Publiez votre trajet longue distance et partagez les frais avec des passagers vérifiés." canonical="/covoiturage/publier" />
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#F26419] flex items-center justify-center"><Car className="w-7 h-7 text-white" /></div>
          <h1 className="font-head text-3xl font-black text-[#1A1A1A]">Publier un trajet</h1>
        </div>

        {verif && !verif.verified && (
          <Link to="/covoiturage/mes-trajets" data-testid="publish-verif-banner"
            className={`block rounded-2xl border-2 p-4 hover:border-[#F26419] transition-colors ${docsMissing ? "border-[#DC2626] bg-[#DC2626]/10" : "border-[#F26419]/40 bg-[#F26419]/10"}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${docsMissing ? "text-[#DC2626]" : "text-[#F26419]"}`} />
              <div className="text-sm">
                <div className="font-bold text-[#1A1A1A]">
                  {docsMissing ? "🚫 Documents obligatoires manquants" :
                   verif.status === "pending" ? "Vérification en cours" :
                   verif.status === "rejected" ? "Vérification rejetée" : "Compte non vérifié"}
                </div>
                <div className="text-xs text-[#3B423E] mt-1">
                  {docsMissing
                    ? <><b>Impossible de publier un trajet</b> sans avoir téléversé votre <b>permis de conduire</b> et votre <b>carte grise</b>. <span className="text-[#DC2626] font-bold underline">Téléverser mes documents maintenant →</span></>
                    : <>Les passagers voient un badge <b>« Vérifié »</b> uniquement sur les trajets des conducteurs dont les documents ont été validés. <span className="text-[#F26419] font-bold underline">Voir mes documents →</span></>}
                </div>
                {verif.status === "rejected" && verif.rejected_reason && (
                  <div className="text-xs text-[#DC2626] mt-1">Motif : {verif.rejected_reason}</div>
                )}
              </div>
            </div>
          </Link>
        )}

        <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#E2DFD8] space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Départ</label>
              <CityInput testid="pub-from" value={form.from_city} onChange={(v) => setForm({ ...form, from_city: v })} cities={cities} placeholder="Bruxelles" /></div>
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Arrivée</label>
              <CityInput testid="pub-to" value={form.to_city} onChange={(v) => setForm({ ...form, to_city: v })} cities={cities} placeholder="Paris" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Date</label>
              <input data-testid="pub-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm outline-none focus:border-[#059669]" /></div>
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Heure</label>
              <input data-testid="pub-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm outline-none focus:border-[#059669]" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Places disponibles</label>
              <input data-testid="pub-seats" type="number" min="1" max="8" value={form.seats_total}
                onChange={(e) => setForm({ ...form, seats_total: Math.max(1, Number(e.target.value)) })}
                className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm outline-none focus:border-[#059669]" /></div>
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Prix par place (€)</label>
              <input data-testid="pub-price" type="number" step="0.5" min="0" value={form.price_per_seat_eur}
                onChange={(e) => setForm({ ...form, price_per_seat_eur: Number(e.target.value) })}
                className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm outline-none focus:border-[#059669]" /></div>
          </div>
          <div><label className="block text-xs font-bold text-[#75807A] mb-1">Point de rendez-vous</label>
            <input data-testid="pub-meeting" value={form.meeting_point}
              onChange={(e) => setForm({ ...form, meeting_point: e.target.value })}
              placeholder="Ex: Gare du Midi Bruxelles, Sortie 3"
              className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm outline-none focus:border-[#059669]" /></div>

          {priceCap && (
            <div className="rounded-xl bg-[#F26419]/10 border border-[#F26419]/30 p-3 text-xs text-[#3B423E]">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-[#F26419]" />
                <div>
                  Distance estimée : <b>{priceCap.distance_km} km</b> · Coût réel : <b>{(priceCap.distance_km * COST_PER_KM_EUR).toFixed(2)} €</b><br />
                  Prix max par place (coût / {form.seats_total}) : <b className="text-[#F26419]">{priceCap.cap.toFixed(2)} €</b>
                  {form.price_per_seat_eur > priceCap.cap && (
                    <div className="text-[#DC2626] font-bold mt-1">⚠️ Prix dépassant le plafond — sera ajusté automatiquement à la publication (aucun bénéfice conducteur).</div>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Toggle Trajet Récurrent */}
          <div className="rounded-xl bg-[#F0FDF4] border border-[#059669]/30 p-3">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input data-testid="pub-recurring-toggle" type="checkbox"
                checked={recurringWeeks > 1}
                onChange={(e) => setRecurringWeeks(e.target.checked ? 4 : 1)}
                className="w-4 h-4 accent-[#059669]" />
              <span className="font-bold text-[#1A1A1A]">🔁 Répéter chaque semaine</span>
            </label>
            {recurringWeeks > 1 && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-[#3B423E]">Nombre de semaines consécutives :</span>
                <select data-testid="pub-recurring-weeks" value={recurringWeeks}
                  onChange={(e) => setRecurringWeeks(Number(e.target.value))}
                  className="h-8 rounded-lg border border-[#E2DFD8] px-2 text-xs font-bold">
                  {[2, 3, 4, 6, 8, 12].map((n) => <option key={n} value={n}>{n} semaines</option>)}
                </select>
                <span className="text-[#75807A]">→ {recurringWeeks} trajets identiques créés</span>
              </div>
            )}
          </div>
          <button data-testid="pub-submit" onClick={submit} disabled={busy || docsMissing}
            className="w-full h-12 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? "Publication…" : docsMissing ? "🚫 Téléverser d'abord vos documents" : recurringWeeks > 1 ? `Publier ${recurringWeeks} trajets hebdomadaires` : "Publier le trajet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------- TRIP DETAILS + BOOK
export function CarpoolTrip() {
  const { id } = useParams();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [seats, setSeats] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/carpool/trips/${id}`).then(r => setTrip(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (user) { setName(user.name || ""); setEmail(user.email || ""); setPhone(user.phone || ""); }
  }, [user]);

  const book = async () => {
    if (!user) { toast.error("Connectez-vous pour réserver"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/carpool/trips/${id}/book`, {
        seats, passenger_email: email, passenger_name: name, passenger_phone: phone,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setBusy(false); }
  };

  if (!trip) return <div className="min-h-screen flex items-center justify-center bg-[#FDF8F0] text-[#75807A]"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…</div>;
  const total = (trip.price_per_seat_eur * seats).toFixed(2);
  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-trip-page">
      <SEOHead title={`${trip.from_city} → ${trip.to_city} · Nold Go Covoiturage`}
        description={`Trajet ${trip.from_city} → ${trip.to_city} le ${trip.date} à ${trip.time}. ${trip.seats_left} place(s) à ${trip.price_per_seat_eur}€.`}
        canonical={`/covoiturage/trajet/${trip.id}`} />
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        <div className="bg-white rounded-2xl p-5 border border-[#E2DFD8]">
          <div className="flex items-center gap-2 text-2xl font-black text-[#1A1A1A]">
            {trip.from_city} <ArrowRight className="w-5 h-5" /> {trip.to_city}
          </div>
          <div className="mt-2 text-sm text-[#3B423E] space-y-1">
            <div><Calendar className="inline w-4 h-4 mr-1" /> {trip.date} à {trip.time} · durée ~{trip.duration_h}h · {trip.distance_km} km</div>
            <div><MapPin className="inline w-4 h-4 mr-1" /> Rendez-vous : <b>{trip.meeting_point}</b></div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Users className="w-4 h-4" /> {trip.seats_left}/{trip.seats_total} places libres
            </div>
            <div className="pt-2"><DriverSnapshot trip={trip} /></div>
            <div className="pt-2 text-2xl font-black text-[#059669]"><Euro className="inline w-6 h-6" />{trip.price_per_seat_eur.toFixed(2)} <span className="text-xs text-[#75807A] font-normal">/ place</span></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-[#E2DFD8] space-y-3">
          <h2 className="font-head text-xl font-black">Réserver</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-bold text-[#75807A] mb-1">Places</label>
              <input data-testid="book-seats" type="number" min="1" max={trip.seats_left} value={seats}
                onChange={(e) => setSeats(Math.min(trip.seats_left, Math.max(1, Number(e.target.value))))}
                className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" /></div>
            <div className="text-right">
              <div className="text-xs text-[#75807A] pt-1">Total à payer</div>
              <div className="text-2xl font-black text-[#059669]">{total} €</div>
            </div>
          </div>
          <input data-testid="book-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom complet"
            className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
          <input data-testid="book-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
            className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
          <input data-testid="book-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Téléphone (optionnel)"
            className="w-full h-12 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
          <button data-testid="book-submit" onClick={book} disabled={busy || !name || !email}
            className="w-full h-12 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-sm disabled:opacity-50">
            {busy ? "Redirection Stripe…" : `Payer ${total} € via Stripe`}
          </button>
          <p className="text-[10px] text-[#75807A] text-center">Paiement sécurisé Stripe · Commission Nold Go 10 % · Email de confirmation automatique</p>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------- CONNECT + VERIFICATION cards
function ConnectCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/driver/stripe-connect/status").then(r => setStatus(r.data)).catch(() => setStatus({ connected: false }));
  }, []);
  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/driver/stripe-connect/onboard", {});
      window.location.href = data.onboarding_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setBusy(false); }
  };
  const dashboard = async () => {
    try {
      const { data } = await api.post("/driver/stripe-connect/dashboard-link", {});
      window.open(data.url, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  if (!status) return null;
  const active = status.connected && status.payouts_enabled;
  return (
    <div data-testid="carpool-connect-card"
      className={`rounded-2xl p-4 border-2 ${active ? "border-[#059669] bg-[#059669]/5" : "border-[#F26419] bg-[#F26419]/5"}`}>
      <div className="flex items-start gap-3">
        <Euro className={`w-6 h-6 shrink-0 mt-0.5 ${active ? "text-[#059669]" : "text-[#F26419]"}`} />
        <div className="flex-1">
          <div className="font-bold text-[#1A1A1A]">
            {active ? "Payouts automatiques Stripe actifs" : "Activer les payouts automatiques"}
          </div>
          <div className="text-xs text-[#3B423E] mt-1">
            {active
              ? `Nold Go reverse automatiquement ${100 - (status.share_pct ? (100 - status.share_pct) : 10)} % à votre compte bancaire à chaque réservation payée. Aucune action manuelle.`
              : status.connected
                ? "Onboarding Stripe en cours — complétez les informations pour recevoir vos paiements directement sur votre RIB."
                : "Créez votre compte de paiement Stripe (Express) pour recevoir automatiquement 90 % du prix de chaque place vendue."}
          </div>
          <div className="mt-3 flex gap-2 flex-wrap">
            {!active && (
              <button data-testid="carpool-connect-onboard" onClick={onboard} disabled={busy}
                className="px-4 py-2 rounded-full bg-[#F26419] hover:bg-[#D85312] text-white text-xs font-bold disabled:opacity-50 inline-flex items-center gap-1.5">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
                {status.connected ? "Reprendre l'onboarding" : "Connecter mon compte Stripe"}
              </button>
            )}
            {status.connected && (
              <button onClick={dashboard} data-testid="carpool-connect-dashboard"
                className="px-4 py-2 rounded-full bg-white border border-[#E2DFD8] text-[#1A1A1A] text-xs font-bold hover:bg-[#F0EBE1] inline-flex items-center gap-1.5">
                <ExternalLink className="w-3 h-3" /> Tableau de bord Stripe
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function VerificationCard() {
  const [status, setStatus] = useState(null);
  const [license, setLicense] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [idDoc, setIdDoc] = useState(null);
  const [plate, setPlate] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/carpool/verification/status").then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!license || !registration) { toast.error("Ajoutez au moins les 2 pièces obligatoires (permis + carte grise)"); return; }
    if (!plate.trim() || !model.trim()) { toast.error("Plaque + modèle véhicule requis"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("license", license);
    fd.append("registration", registration);
    if (idDoc) fd.append("id_document", idDoc);
    fd.append("plate", plate);
    fd.append("model", model);
    try {
      await api.post("/carpool/verification/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documents envoyés — validation admin sous 24 h");
      setLicense(null); setRegistration(null); setIdDoc(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  const badge = status.verified ? { label: "Compte vérifié", cls: "text-[#059669] border-[#059669] bg-[#059669]/5" }
    : status.status === "pending" ? { label: "En cours de vérification", cls: "text-[#F26419] border-[#F26419] bg-[#F26419]/5" }
    : status.status === "rejected" ? { label: "Documents rejetés — réenvoyer", cls: "text-[#DC2626] border-[#DC2626] bg-[#DC2626]/5" }
    : { label: "Non vérifié", cls: "text-[#75807A] border-[#E2DFD8] bg-[#F0EBE1]" };
  return (
    <div data-testid="carpool-verif-card" className={`rounded-2xl p-4 border-2 ${badge.cls}`}>
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-bold text-[#1A1A1A] inline-flex items-center gap-2">
            {badge.label}
          </div>
          <div className="text-xs text-[#3B423E] mt-1">
            Téléversez votre permis de conduire (recto/verso) et la carte grise du véhicule.
            Après validation par notre équipe, un badge <b>« Vérifié »</b> apparaît sur tous vos trajets et rassure les passagers.
          </div>

          {status.verified ? (
            <div className="mt-2 text-xs text-[#3B423E]">
              Véhicule : <b>{status.vehicle_model}</b> · Plaque : <b>{status.vehicle_plate}</b>
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="block">
                <span className="block text-[10px] font-bold text-[#75807A] mb-1">Permis de conduire</span>
                <input data-testid="verif-license" type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={(e) => setLicense(e.target.files?.[0] || null)}
                  className="text-xs w-full" />
              </label>
              <label className="block">
                <span className="block text-[10px] font-bold text-[#75807A] mb-1">Carte grise</span>
                <input data-testid="verif-registration" type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={(e) => setRegistration(e.target.files?.[0] || null)}
                  className="text-xs w-full" />
              </label>
              <label className="block sm:col-span-2">
                <span className="block text-[10px] font-bold text-[#75807A] mb-1">Pièce d'identité (CNI ou passeport) <span className="text-[#75807A] font-normal">— recommandé pour un badge « Identité vérifiée »</span></span>
                <input data-testid="verif-id" type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={(e) => setIdDoc(e.target.files?.[0] || null)}
                  className="text-xs w-full" />
              </label>
              <input data-testid="verif-plate" value={plate} onChange={(e) => setPlate(e.target.value)}
                placeholder="Plaque (ex: 1-ABC-234)"
                className="h-10 rounded-lg border border-[#E2DFD8] px-3 text-xs" />
              <input data-testid="verif-model" value={model} onChange={(e) => setModel(e.target.value)}
                placeholder="Marque + modèle (ex: Peugeot 308)"
                className="h-10 rounded-lg border border-[#E2DFD8] px-3 text-xs" />
              <button data-testid="verif-submit" onClick={submit} disabled={busy}
                className="sm:col-span-2 h-10 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                {status.status === "pending" ? "Renvoyer les documents" : "Envoyer pour vérification"}
              </button>
            </div>
          )}
          {status.status === "rejected" && status.rejected_reason && (
            <div className="mt-2 text-xs text-[#DC2626]">Motif du rejet : {status.rejected_reason}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------- DRIVER DASHBOARD
export function CarpoolMine() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [chatBookingId, setChatBookingId] = useState(null);
  const load = () => api.get("/carpool/mine")
    .then((r) => setData(r.data))
    .catch((e) => {
      setError(e?.response?.data?.detail || "Impossible de charger vos trajets.");
      setData({ trips: [], total_earnings_eur: 0 });
    });
  useEffect(() => {
    if (!user) { nav("/login"); return; }
    load();
  }, [user]);

  const cancelTrip = async (id) => {
    const reason = window.prompt("Motif de l'annulation (visible par les passagers) :");
    if (reason === null) return;
    if (!window.confirm("Confirmer ? Tous les passagers payés seront intégralement remboursés.")) return;
    try {
      const { data: res } = await api.post(`/carpool/trips/${id}/cancel`, { reason });
      toast.success(`Trajet annulé — ${res.refunded} remboursement(s), ${res.mails_sent} email(s) envoyé(s).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FDF8F0] text-[#75807A]"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…</div>;
  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-mine-page">
      <SEOHead title="Mes trajets covoiturage — Nold Go" description="Gérez vos trajets covoiturage publiés." canonical="/covoiturage/mes-trajets" noindex />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-head text-3xl font-black">Mes trajets</h1>
          <Link to="/covoiturage/publier" className="px-4 py-2 rounded-full bg-[#059669] text-white text-sm font-bold">+ Publier</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <ConnectCard />
          <VerificationCard />
        </div>

        {error && (
          <div className="rounded-2xl bg-[#DC2626]/10 border border-[#DC2626]/30 p-3 text-sm text-[#DC2626] mb-4">
            ⚠️ {error}
          </div>
        )}

        <div className="rounded-2xl bg-[#059669] text-white p-5 mb-4">
          <div className="text-xs uppercase tracking-widest opacity-80">Revenus totaux (90 % après commission)</div>
          <div className="text-4xl font-black mt-1">{data.total_earnings_eur.toFixed(2)} €</div>
        </div>
        {data.trips.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] p-8 text-center text-[#75807A]">
            Vous n'avez pas encore publié de trajet.
          </div>
        ) : data.trips.map((t) => (
          <div key={t.id} data-testid={`mine-trip-${t.id}`} className="bg-white rounded-2xl p-4 border border-[#E2DFD8] mb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-bold text-lg">
                  {t.from_city} → {t.to_city}
                  {t.status === "cancelled" && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#DC2626]/10 text-[#DC2626] font-bold">Annulé</span>}
                </div>
                <div className="text-xs text-[#75807A]">{t.date} · {t.time} · {t.seats_left}/{t.seats_total} libres · {t.price_per_seat_eur}€/place</div>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <div className="text-right">
                  <div className="text-xs text-[#75807A]">Passagers</div>
                  <div className="text-xl font-black text-[#F26419]">{t.bookings.length}</div>
                </div>
                {t.status === "active" && (
                  <button data-testid={`cancel-trip-${t.id}`} onClick={() => cancelTrip(t.id)}
                    className="text-[10px] px-2 py-1 rounded-full border border-[#DC2626]/40 text-[#DC2626] font-bold hover:bg-[#DC2626]/5">
                    Annuler ce trajet
                  </button>
                )}
              </div>
            </div>
            {t.bookings.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#E2DFD8] text-xs space-y-1">
                {t.bookings.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-[#059669]" /> {b.passenger_name} · {b.seats} place(s)</span>
                    <div className="inline-flex items-center gap-2">
                      <button data-testid={`chat-open-${b.id}`} onClick={() => setChatBookingId(b.id)}
                        className="text-[#059669] font-bold hover:underline">💬 Discuter</button>
                      <span className="font-bold">{b.driver_payout_eur.toFixed(2)} €</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {chatBookingId && (
          <CarpoolChatModal bookingId={chatBookingId} currentUserId={user?.id}
            onClose={() => setChatBookingId(null)} />
        )}
      </div>
    </div>
  );
}

export default CarpoolSearch;
